import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { API_BASE_URL, getAuthHeaders, getCustomerId, getStoredUser } from '../auth';
import { EmptyPane, ErrorBanner, LoadingPane, PageHeader } from '../components/page-elements';
import { useNavigate } from 'react-router-dom';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'available', label: 'Available' },
  { value: 'depleted', label: 'Depleted' },
  { value: 'archived', label: 'Archived' },
];

const formatWeight = (value) => Number(value || 0).toLocaleString();

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString();
};

const statusChipColor = (status) => {
  if (status === 'available') return 'success';
  if (status === 'depleted') return 'warning';
  return 'default';
};

const readErrorMessage = async (response, fallback) => {
  const contentType = String(response.headers.get('content-type') || '').toLowerCase();
  if (contentType.includes('application/json')) {
    const payload = await response.json().catch(() => ({}));
    const detail = Array.isArray(payload?.errors)
      ? payload.errors.map((row) => row.msg).join(', ')
      : payload?.message;
    return detail || `${fallback} (HTTP ${response.status})`;
  }

  const text = await response.text().catch(() => '');
  const trimmed = String(text || '').trim();
  if (trimmed) return `${fallback} (HTTP ${response.status}): ${trimmed.slice(0, 200)}`;
  return `${fallback} (HTTP ${response.status})`;
};

const GroundInventory = () => {
  const navigate = useNavigate();
  const user = useMemo(() => getStoredUser(), []);
  const isInternal = user?.role === 'internal' || user?.role === 'admin';
  const isCustomer = user?.role === 'customer';
  const customerScopeId = useMemo(() => getCustomerId(user), [user]);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingLotId, setDeletingLotId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [lots, setLots] = useState([]);
  const [summaryByMaterial, setSummaryByMaterial] = useState([]);
  const [totals, setTotals] = useState({ totalStartingWeight: 0, totalRemainingWeight: 0 });

  const [customers, setCustomers] = useState([]);
  const [materials, setMaterials] = useState([]);

  const [customerFilter, setCustomerFilter] = useState('all');
  const [materialFilter, setMaterialFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('available');
  const [search, setSearch] = useState('');
  const [selectedLotId, setSelectedLotId] = useState('');
  const [lotAllocations, setLotAllocations] = useState([]);
  const [lotAllocationsLoading, setLotAllocationsLoading] = useState(false);
  const [lotAllocationsError, setLotAllocationsError] = useState('');

  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [adjustMode, setAdjustMode] = useState('create');
  const [adjustmentForm, setAdjustmentForm] = useState({
    lotId: '',
    customerName: '',
    materialName: '',
    sourceRailcarID: '',
    startingWeight: '',
    remainingWeight: '',
    notes: '',
  });

  useEffect(() => {
    if (!isCustomer) return;
    setCustomerFilter(customerScopeId || 'all');
  }, [isCustomer, customerScopeId]);

  const fetchReferenceData = useCallback(async () => {
    if (!isInternal) return;

    const [customerRes, materialRes] = await Promise.all([
      fetch(`${API_BASE_URL}/api/customers`, { headers: getAuthHeaders() }),
      fetch(`${API_BASE_URL}/api/materials`, { headers: getAuthHeaders() }),
    ]);

    if (!customerRes.ok) throw new Error('Failed to load customers');
    if (!materialRes.ok) throw new Error('Failed to load materials');

    const customerRows = await customerRes.json();
    const materialRows = await materialRes.json();

    setCustomers(Array.isArray(customerRows) ? customerRows : []);
    setMaterials(
      Array.isArray(materialRows)
        ? materialRows.filter((material) => material?.isActive !== false)
        : []
    );
  }, [isInternal]);

  const fetchLots = useCallback(async () => {
    const params = new URLSearchParams();

    if (isCustomer && customerScopeId) {
      params.set('customerId', String(customerScopeId));
    } else if (isInternal && customerFilter !== 'all') {
      params.set('customerId', String(customerFilter));
    }

    if (materialFilter !== 'all') params.set('materialId', String(materialFilter));
    if (statusFilter !== 'all') {
      params.set('status', statusFilter);
    } else {
      params.set('includeAllStatuses', 'true');
    }

    const response = await fetch(`${API_BASE_URL}/api/ground-inventory?${params.toString()}`, {
      headers: getAuthHeaders(),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload?.message || 'Failed to load ground inventory');
    }

    setLots(Array.isArray(payload?.lots) ? payload.lots : []);
    setSummaryByMaterial(Array.isArray(payload?.summaryByMaterial) ? payload.summaryByMaterial : []);
    setTotals(payload?.totals || { totalStartingWeight: 0, totalRemainingWeight: 0 });
  }, [isCustomer, customerScopeId, isInternal, customerFilter, materialFilter, statusFilter]);

  const refreshPage = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      await Promise.all([fetchReferenceData(), fetchLots()]);
    } catch (err) {
      setError(err.message || 'Failed to load ground inventory');
    } finally {
      setLoading(false);
    }
  }, [fetchReferenceData, fetchLots]);

  useEffect(() => {
    refreshPage();
  }, [refreshPage]);

  useEffect(() => {
    if (isCustomer) return;
    if (!adjustDialogOpen) return;

    const scopedCustomerId = adjustmentForm.customerName || customerFilter;
    if (!scopedCustomerId || scopedCustomerId === 'all') return;

    const matchingMaterials = materials.filter((material) => {
      const materialCustomerId = String(material?.customerName?._id || material?.customerName || '');
      return materialCustomerId === String(scopedCustomerId);
    });

    const currentExists = matchingMaterials.some((material) => material._id === adjustmentForm.materialName);
    if (!currentExists) {
      setAdjustmentForm((prev) => ({
        ...prev,
        materialName: matchingMaterials[0]?._id || '',
      }));
    }
  }, [adjustDialogOpen, adjustmentForm.customerName, adjustmentForm.materialName, customerFilter, isCustomer, materials]);

  const materialFilterOptions = useMemo(() => {
    const scopedCustomerId = isCustomer ? customerScopeId : customerFilter;
    if (!scopedCustomerId || scopedCustomerId === 'all') return materials;
    return materials.filter((material) => {
      const materialCustomerId = String(material?.customerName?._id || material?.customerName || '');
      return materialCustomerId === String(scopedCustomerId);
    });
  }, [materials, customerFilter, isCustomer, customerScopeId]);

  const adjustmentMaterialOptions = useMemo(() => {
    const scopedCustomerId = adjustmentForm.customerName || (isCustomer ? customerScopeId : customerFilter);
    if (!scopedCustomerId || scopedCustomerId === 'all') return [];
    return materials.filter((material) => {
      const materialCustomerId = String(material?.customerName?._id || material?.customerName || '');
      return materialCustomerId === String(scopedCustomerId);
    });
  }, [adjustmentForm.customerName, customerFilter, isCustomer, customerScopeId, materials]);

  const visibleLots = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return lots;

    return lots.filter((lot) => {
      const haystack = [
        lot?.customerName?.customerName,
        lot?.materialName?.materialName,
        lot?.materialName?.refNum,
        lot?.sourceRailcarID,
        lot?.sourceType,
        lot?.status,
        lot?.notes,
      ]
        .map((value) => String(value || '').toLowerCase())
        .join(' ');

      return haystack.includes(term);
    });
  }, [lots, search]);

  const selectedLot = useMemo(
    () => visibleLots.find((lot) => String(lot._id) === String(selectedLotId || '')) || null,
    [visibleLots, selectedLotId]
  );

  useEffect(() => {
    if (!visibleLots.length) {
      setSelectedLotId('');
      return;
    }
    const exists = visibleLots.some((lot) => String(lot._id) === String(selectedLotId || ''));
    if (!exists) {
      setSelectedLotId(String(visibleLots[0]._id));
    }
  }, [visibleLots, selectedLotId]);

  useEffect(() => {
    if (!isInternal || !selectedLotId) {
      setLotAllocations([]);
      setLotAllocationsError('');
      return;
    }

    const params = new URLSearchParams({ lotId: String(selectedLotId) });
    setLotAllocationsLoading(true);
    setLotAllocationsError('');
    fetch(`${API_BASE_URL}/api/ground-inventory/allocations?${params.toString()}`, {
      headers: getAuthHeaders(),
    })
      .then(async (res) => {
        const payload = await res.json().catch(() => ([]));
        if (!res.ok) {
          const message = payload?.message || 'Failed to load lot activity';
          throw new Error(message);
        }
        return Array.isArray(payload) ? payload : [];
      })
      .then((rows) => setLotAllocations(rows))
      .catch((err) => {
        setLotAllocations([]);
        setLotAllocationsError(err.message || 'Failed to load lot activity');
      })
      .finally(() => setLotAllocationsLoading(false));
  }, [isInternal, selectedLotId]);

  const resetAdjustmentForm = () => {
    setAdjustmentForm({
      lotId: '',
      customerName: isCustomer ? String(customerScopeId || '') : customerFilter !== 'all' ? String(customerFilter) : '',
      materialName: '',
      sourceRailcarID: '',
      startingWeight: '',
      remainingWeight: '',
      notes: '',
    });
  };

  const openAdjustmentDialog = () => {
    setAdjustMode('create');
    resetAdjustmentForm();
    setAdjustDialogOpen(true);
    setError('');
    setSuccess('');
  };

  const openEditAdjustmentDialog = (lot) => {
    setAdjustMode('edit');
    setAdjustmentForm({
      lotId: String(lot?._id || ''),
      customerName: String(lot?.customerName?._id || lot?.customerName || ''),
      materialName: String(lot?.materialName?._id || lot?.materialName || ''),
      sourceRailcarID: String(lot?.sourceRailcarID || ''),
      startingWeight: String(Number(lot?.startingWeight || 0)),
      remainingWeight: String(Number(lot?.remainingWeight || 0)),
      notes: String(lot?.notes || ''),
    });
    setAdjustDialogOpen(true);
    setError('');
    setSuccess('');
  };

  const closeAdjustmentDialog = () => {
    setAdjustDialogOpen(false);
    resetAdjustmentForm();
  };

  const handleSaveAdjustment = async () => {
    const isEditMode = adjustMode === 'edit' && String(adjustmentForm.lotId || '').trim();
    const customerName = String(adjustmentForm.customerName || '').trim();
    const materialName = String(adjustmentForm.materialName || '').trim();
    const startingWeight = Number(adjustmentForm.startingWeight);
    const hasRemaining = String(adjustmentForm.remainingWeight || '').trim() !== '';
    const remainingWeight = hasRemaining ? Number(adjustmentForm.remainingWeight) : null;

    if (!customerName) {
      setError('Please select a customer.');
      return;
    }
    if (!materialName) {
      setError('Please select a material.');
      return;
    }
    if (!Number.isFinite(startingWeight) || startingWeight < 0) {
      setError('Starting weight must be 0 or greater.');
      return;
    }
    if (hasRemaining && (!Number.isFinite(remainingWeight) || remainingWeight < 0)) {
      setError('Remaining weight must be 0 or greater.');
      return;
    }
    if (hasRemaining && remainingWeight > startingWeight) {
      setError('Remaining weight cannot exceed starting weight.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(
        isEditMode
          ? `${API_BASE_URL}/api/ground-inventory/adjustments/${adjustmentForm.lotId}`
          : `${API_BASE_URL}/api/ground-inventory/adjustments`,
        {
        method: isEditMode ? 'PUT' : 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          customerName,
          materialName,
          sourceRailcarID: String(adjustmentForm.sourceRailcarID || '').trim(),
          startingWeight,
          remainingWeight: hasRemaining ? remainingWeight : isEditMode ? startingWeight : undefined,
          notes: String(adjustmentForm.notes || '').trim(),
        }),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, isEditMode ? 'Failed to update adjustment' : 'Failed to create adjustment'));
      }

      setSuccess(isEditMode ? 'Ground inventory lot updated.' : 'Ground inventory lot added.');
      closeAdjustmentDialog();
      await refreshPage();
    } catch (err) {
      setError(err.message || `Failed to ${isEditMode ? 'update' : 'create'} adjustment`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAdjustment = async (lot) => {
    const lotId = String(lot?._id || '').trim();
    if (!lotId) return;

    const confirmed = window.confirm(
      `Delete manual adjustment lot for ${lot?.materialName?.materialName || 'material'}? This cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingLotId(lotId);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/ground-inventory/adjustments/${lotId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, 'Failed to delete adjustment'));
      }

      setSuccess('Ground inventory lot deleted.');
      await refreshPage();
    } catch (err) {
      setError(err.message || 'Failed to delete adjustment');
    } finally {
      setDeletingLotId('');
    }
  };

  if (loading) return <LoadingPane label="Loading ground inventory..." />;

  return (
    <Box p={2}>
      <PageHeader
        title="Ground Inventory"
        subtitle="Track lots converted from railcars and manual on-ground adjustments."
        actions={
          <>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refreshPage}>
              Refresh
            </Button>
            {isInternal ? (
              <Button variant="contained" startIcon={<AddIcon />} onClick={openAdjustmentDialog}>
                Add Lot
              </Button>
            ) : null}
          </>
        }
      />

      <ErrorBanner message={error} />
      {success ? (
        <Typography color="primary" sx={{ mb: 1.5 }}>
          {success}
        </Typography>
      ) : null}

      <Grid container spacing={1.2} sx={{ mb: 1.5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined"><CardContent><Typography variant="caption" color="text.secondary">Total Starting Weight</Typography><Typography variant="h6" fontWeight={700}>{formatWeight(totals.totalStartingWeight)} lbs</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined"><CardContent><Typography variant="caption" color="text.secondary">Total Remaining Weight</Typography><Typography variant="h6" fontWeight={700}>{formatWeight(totals.totalRemainingWeight)} lbs</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined"><CardContent><Typography variant="caption" color="text.secondary">Lots</Typography><Typography variant="h6" fontWeight={700}>{formatWeight(lots.length)}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined"><CardContent><Typography variant="caption" color="text.secondary">Materials</Typography><Typography variant="h6" fontWeight={700}>{formatWeight(summaryByMaterial.length)}</Typography></CardContent></Card>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2 }}>
        <Grid container spacing={1.2}>
          {isInternal ? (
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="ground-customer-filter-label">Customer</InputLabel>
                <Select
                  labelId="ground-customer-filter-label"
                  label="Customer"
                  value={customerFilter}
                  onChange={(event) => {
                    setCustomerFilter(event.target.value);
                    setMaterialFilter('all');
                  }}
                >
                  <MenuItem value="all">All Customers</MenuItem>
                  {customers.map((customer) => (
                    <MenuItem key={customer._id} value={customer._id}>
                      {customer.customerName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ) : null}

          <Grid item xs={12} md={isInternal ? 3 : 4}>
            <FormControl fullWidth size="small">
              <InputLabel id="ground-material-filter-label">Material</InputLabel>
              <Select
                labelId="ground-material-filter-label"
                label="Material"
                value={materialFilter}
                onChange={(event) => setMaterialFilter(event.target.value)}
              >
                <MenuItem value="all">All Materials</MenuItem>
                {materialFilterOptions.map((material) => (
                  <MenuItem key={material._id} value={material._id}>
                    {material.materialName} {material.refNum ? `(${material.refNum})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={isInternal ? 2 : 3}>
            <FormControl fullWidth size="small">
              <InputLabel id="ground-status-filter-label">Status</InputLabel>
              <Select
                labelId="ground-status-filter-label"
                label="Status"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={isInternal ? 4 : 5}>
            <TextField
              fullWidth
              size="small"
              label="Search Lots"
              placeholder="customer, material, source railcar, notes"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {visibleLots.length === 0 ? (
        <EmptyPane title="No ground inventory lots" subtitle="Try changing filters or add a new lot." />
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell>Material</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Starting</TableCell>
                  <TableCell align="right">Remaining</TableCell>
                  <TableCell>Received</TableCell>
                  <TableCell>Notes</TableCell>
                  {isInternal ? <TableCell align="right">Actions</TableCell> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleLots.map((lot) => (
                  <TableRow
                    key={lot._id}
                    hover
                    selected={isInternal && String(selectedLotId) === String(lot._id)}
                    onClick={() => {
                      if (!isInternal) return;
                      setSelectedLotId(String(lot._id));
                    }}
                    sx={isInternal ? { cursor: 'pointer' } : undefined}
                  >
                    <TableCell>{lot?.customerName?.customerName || 'N/A'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {lot?.materialName?.materialName || 'Material'}
                      </Typography>
                      {lot?.materialName?.refNum ? (
                        <Typography variant="caption" color="text.secondary">
                          Ref: {lot.materialName.refNum}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography variant="body2">{lot?.sourceType || 'N/A'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {lot?.sourceRailcarID || 'No railcar source'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={statusChipColor(lot?.status)} label={String(lot?.status || 'unknown')} />
                    </TableCell>
                    <TableCell align="right">{formatWeight(lot?.startingWeight)}</TableCell>
                    <TableCell align="right">{formatWeight(lot?.remainingWeight)}</TableCell>
                    <TableCell>{formatDateTime(lot?.receivedAt)}</TableCell>
                    <TableCell>{lot?.notes || '-'}</TableCell>
                    {isInternal ? (
                      <TableCell align="right">
                        {lot?.sourceType === 'manual_adjustment' ? (
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Button size="small" variant="outlined" onClick={() => openEditAdjustmentDialog(lot)}>
                              Edit
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              disabled={deletingLotId === String(lot?._id || '')}
                              onClick={() => handleDeleteAdjustment(lot)}
                            >
                              {deletingLotId === String(lot?._id || '') ? 'Deleting...' : 'Delete'}
                            </Button>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Railcar Conversion
                          </Typography>
                        )}
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}

      {isInternal && selectedLot ? (
        <Card variant="outlined" sx={{ mt: 1.5, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Lot Activity
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
              Selected Lot: {selectedLot.sourceRailcarID || selectedLot._id}
            </Typography>
            {lotAllocationsError ? (
              <Typography variant="body2" color="error">{lotAllocationsError}</Typography>
            ) : lotAllocationsLoading ? (
              <Typography variant="body2" color="text.secondary">Loading lot activity...</Typography>
            ) : lotAllocations.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No BOL allocations found for this lot.</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Allocated At</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Order</TableCell>
                      <TableCell align="right">Removed Weight</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lotAllocations.map((allocation) => (
                      <TableRow key={allocation._id}>
                        <TableCell>{formatDateTime(allocation.createdAt)}</TableCell>
                        <TableCell>
                          {allocation.bolId?.projectName?.projectName
                            || allocation.bolId?.orderNumber?.projectName?.projectName
                            || 'N/A'}
                        </TableCell>
                        <TableCell>{allocation.bolId?.orderNumber?.orderNumber || 'N/A'}</TableCell>
                        <TableCell align="right">{formatWeight(allocation.allocatedWeight)} lbs</TableCell>
                        <TableCell align="right">
                          {allocation.bolId?._id ? (
                            <Button size="small" variant="outlined" onClick={() => navigate(`/bols/${allocation.bolId._id}`)}>
                              View BOL
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      ) : null}

      <Dialog open={adjustDialogOpen} onClose={closeAdjustmentDialog} fullWidth maxWidth="sm">
        <DialogTitle>{adjustMode === 'edit' ? 'Edit Ground Inventory Lot' : 'Add Ground Inventory Lot'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.2} sx={{ mt: 0.2 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="adjust-customer-label">Customer</InputLabel>
                <Select
                  labelId="adjust-customer-label"
                  label="Customer"
                  value={adjustmentForm.customerName}
                  onChange={(event) =>
                    setAdjustmentForm((prev) => ({
                      ...prev,
                      customerName: event.target.value,
                      materialName: '',
                    }))
                  }
                >
                  {customers.map((customer) => (
                    <MenuItem key={customer._id} value={customer._id}>
                      {customer.customerName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="adjust-material-label">Material</InputLabel>
                <Select
                  labelId="adjust-material-label"
                  label="Material"
                  value={adjustmentForm.materialName}
                  onChange={(event) => setAdjustmentForm((prev) => ({ ...prev, materialName: event.target.value }))}
                >
                  {adjustmentMaterialOptions.map((material) => (
                    <MenuItem key={material._id} value={material._id}>
                      {material.materialName} {material.refNum ? `(${material.refNum})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Railcar ID (Optional)"
                fullWidth
                value={adjustmentForm.sourceRailcarID}
                onChange={(event) => setAdjustmentForm((prev) => ({ ...prev, sourceRailcarID: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Starting Weight (lbs)"
                type="number"
                fullWidth
                value={adjustmentForm.startingWeight}
                onChange={(event) => setAdjustmentForm((prev) => ({ ...prev, startingWeight: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Remaining Weight (lbs)"
                type="number"
                fullWidth
                helperText={adjustMode === 'edit' ? '' : 'Optional; defaults to starting weight'}
                value={adjustmentForm.remainingWeight}
                onChange={(event) => setAdjustmentForm((prev) => ({ ...prev, remainingWeight: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                minRows={2}
                value={adjustmentForm.notes}
                onChange={(event) => setAdjustmentForm((prev) => ({ ...prev, notes: event.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAdjustmentDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveAdjustment} disabled={submitting}>
            {submitting ? 'Saving...' : adjustMode === 'edit' ? 'Save Changes' : 'Add Lot'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroundInventory;
